'use client';
import * as React from 'react';
import { DayPicker } from 'react-day-picker';
import { enUS, es, fr, ptBR, zhCN } from 'react-day-picker/locale';
import 'react-day-picker/style.css';
import { useLocale, useTranslations } from 'next-intl';
import { Icon } from '@/components/site/ui';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DatePickerDropdown } from './date-picker-dropdown';

const LOCALES = { pt: ptBR, en: enUS, es, fr, zh: zhCN } as const;

/** yyyy-mm-dd ⇄ local Date (never via new Date('yyyy-mm-dd'), which parses as UTC
 *  and shifts the day west of Greenwich). */
function parseISODate(v?: string | null): Date | undefined {
  if (!v) return undefined;
  const [y, m, d] = v.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function toISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * shadcn-style date picker: Input-look trigger + react-day-picker in a popover.
 * Value is a plain `yyyy-mm-dd` string (same contract as the native date input
 * it replaces). `mode` picks sensible bounds: `past` for birth dates (dropdown
 * year nav, no future days), `future` for scheduling (no past days), `any` for
 * filters that span both sides of today (no restriction).
 */
export function DatePicker({
  id,
  value,
  onChange,
  mode = 'past',
  placeholder,
  compact = false,
  inline = false,
  availableDates,
  hideNavigation = false,
  disabled = false,
  className,
  'aria-label': ariaLabel,
}: {
  id?: string;
  value?: string | null;
  onChange: (v: string | null) => void;
  mode?: 'past' | 'future' | 'any';
  /** Empty-state label; defaults to the shared "select date" copy. */
  placeholder?: string;
  /** Numeric short date (e.g. 10/07/2026) instead of the long form — for narrow
   *  triggers like a filter pill. */
  compact?: boolean;
  /** Reuse this calendar without a popover on public scheduling pages. */
  inline?: boolean;
  /** Optional explicit civil dates supplied by a clinic-owned availability response. */
  availableDates?: string[];
  /** Month and year menus only, without the previous/next arrows. */
  hideNavigation?: boolean;
  disabled?: boolean;
  className?: string;
  /** Accessible name for label-less layouts (compact dialog rows). */
  'aria-label'?: string;
}) {
  const locale = useLocale();
  const t = useTranslations('common');
  const [open, setOpen] = React.useState(false);
  const selected = parseISODate(value);
  const today = new Date();

  const calendar = (
    <DayPicker
      mode="single"
      locale={LOCALES[locale as keyof typeof LOCALES] ?? ptBR}
      selected={selected}
      defaultMonth={selected ?? (mode === 'past' ? new Date(today.getFullYear() - 30, 0) : today)}
      captionLayout="dropdown"
      hideNavigation={hideNavigation}
      components={{ Dropdown: DatePickerDropdown }}
      startMonth={
        availableDates?.length
          ? parseISODate(availableDates[0])
          : mode === 'future'
            ? today
            : new Date(today.getFullYear() - 120, 0)
      }
      endMonth={
        availableDates?.length
          ? parseISODate(availableDates.at(-1))
          : mode === 'past'
            ? today
            : new Date(today.getFullYear() + 2, 11)
      }
      disabled={
        availableDates
          ? (date) => !availableDates.includes(toISODate(date))
          : mode === 'past'
            ? { after: today }
            : mode === 'future'
              ? { before: today }
              : undefined
      }
      onSelect={(d) => {
        onChange(d ? toISODate(d) : null);
        setOpen(false);
      }}
      style={
        {
          '--rdp-day-width': inline ? 'auto' : undefined,
          // A square button so the selected day is a circle, not an oval.
          '--rdp-day-height': inline ? '44px' : undefined,
          '--rdp-day_button-width': inline ? '40px' : undefined,
          '--rdp-day_button-height': inline ? '40px' : undefined,
          '--rdp-accent-color': 'var(--medlux-primary)',
          '--rdp-accent-background-color': 'var(--medlux-soft)',
          '--rdp-today-color': 'var(--medlux-soft-foreground)',
        } as React.CSSProperties
      }
      className="text-sm"
      // Inline, the month fills its column: seven equal tracks instead of fixed 34px cells.
      classNames={inline ? { months: 'rdp-months w-full max-w-none', month: 'rdp-month w-full', month_grid: 'rdp-month_grid w-full table-fixed' } : undefined}
    />
  );
  if (inline)
    return (
      <div id={id} aria-label={ariaLabel} className={className}>
        {calendar}
      </div>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground',
            'flex h-11 w-full items-center gap-2.5 rounded-field border border-input bg-white px-3.5 text-left text-sm transition-[border-color,box-shadow]',
            'focus-visible:border-medlux focus-visible:shadow-field-focus focus-visible:outline-none',
            selected ? 'text-foreground' : 'text-neutral-400',
            className,
          )}
        >
          <Icon name="calendar-03" size={18} className="shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">
            {selected
              ? new Intl.DateTimeFormat(locale, { dateStyle: compact ? 'short' : 'long' }).format(
                  selected,
                )
              : (placeholder ?? t('labels.selectDate'))}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-auto rounded-xl bg-white p-3 shadow-xl"
      >
        {calendar}
      </PopoverContent>
    </Popover>
  );
}
